import React, { useState } from 'react'
import Modal from 'aldoo-ra/Modal'
import Typography from 'aldoo-ra/Typography'
import Button from 'aldoo-ra/Button'
import Checkbox from 'aldoo-ra/Checkbox'
import DropZone from 'aldoo-ra/DropZone'
import { ArrowUpTrayIcon } from '@heroicons/react/24/outline'
import { showNotification } from 'aldoo-ra/Notification'
import PubSub from 'pubsub-js'
import CSVFormatExamplePanel from './CSVFormatExamplePanel'
import SubscriberController from '../controllers/SubscriberController'

const ImportSubscribersDialog = ({ isOpen, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false)
  const [file, setFile] = useState(null)
  const [importAsSubscribed, setImportAsSubscribed] = useState(true)
  const [results, setResults] = useState(null)
  const [showFormatExample, setShowFormatExample] = useState(false)
  const [analysisStats, setAnalysisStats] = useState(null)
  const [showConfirmation, setShowConfirmation] = useState(false)

  const handleFileDrop = (acceptedFiles, rejectedFiles) => {
    if (rejectedFiles.length > 0) {
      showNotification({
        message: "Error: Only CSV files are allowed",
        className: "bg-admin_error text-white",
      })
      return
    }

    if (acceptedFiles.length > 0) {
      setFile(acceptedFiles[0])
      setAnalysisStats(null)
      setShowConfirmation(false)
    }
  }

  const handleAnalyze = async (e) => {
    if (e) e.preventDefault()

    if (!file) {
      showNotification({
        message: "Error: Please select a CSV file to import",
        className: "bg-admin_error text-white",
      })
      return
    }

    setLoading(true)
    const response = await SubscriberController.analyzeImport(file)
    setLoading(false)

    if (response.success) {
      setAnalysisStats(response.stats)
      setShowConfirmation(true)
    }
  }

  const handleProceedImport = async () => {
    setLoading(true)
    setShowConfirmation(false)

    const response = await SubscriberController.importSubscribers(file, importAsSubscribed)
    setLoading(false)

    if (response.success) {
      setResults(response.results)
      PubSub.publish('newsletter.subscribers.imported', response.results)
      if (onSuccess) onSuccess()
    }
  }

  const handleClose = () => {
    if (!loading) {
      setFile(null)
      setResults(null)
      setAnalysisStats(null)
      setShowConfirmation(false)
      onClose()
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      variant="admin_compact"
      className="max-w-lg"
    >
      <form onSubmit={handleAnalyze}>
        <div className="p-6">
          <Typography
            component="h1"
            variant="admin_h3"
            className="mb-6 text-center"
          >
            Import Subscribers
          </Typography>

          {!results ? (
            showConfirmation && analysisStats ? (
              <div className="space-y-6">
                <Typography variant="admin_h4" className="mb-4">
                  Preview
                </Typography>

                <div className="bg-admin_canvas rounded-lg p-4 divide-y divide-admin_bordered">
                  <div className="flex justify-between py-2">
                    <Typography variant="admin_txt1">Total Rows:</Typography>
                    <Typography variant="admin_txt1" className="font-semibold">{analysisStats.totalRows.toLocaleString()}</Typography>
                  </div>
                  <div className="flex justify-between py-2">
                    <Typography variant="admin_txt1">Valid Emails:</Typography>
                    <Typography variant="admin_txt1" className="text-admin_primary font-semibold">{analysisStats.validEmails.toLocaleString()}</Typography>
                  </div>
                  {analysisStats.invalidEmails > 0 && (
                    <div className="flex justify-between py-2">
                      <Typography variant="admin_txt1">Invalid Emails:</Typography>
                      <Typography variant="admin_txt1" className="text-admin_error font-semibold">{analysisStats.invalidEmails.toLocaleString()}</Typography>
                    </div>
                  )}
                  <div className="flex justify-between py-2">
                    <Typography variant="admin_txt1">Languages:</Typography>
                    <Typography variant="admin_txt1" className="font-semibold">{analysisStats.languageCount.toLocaleString()}</Typography>
                  </div>
                  <div className="flex justify-between py-2">
                    <Typography variant="admin_txt1">Platforms:</Typography>
                    <Typography variant="admin_txt1" className="font-semibold">{analysisStats.platforms.join(', ')}</Typography>
                  </div>
                </div>

                {analysisStats.errors && analysisStats.errors.length > 0 && (
                  <div className="bg-admin_error/10 border border-admin_error/20 rounded-lg p-4">
                    <Typography variant="admin_body1" className="text-admin_error font-semibold mb-2">
                      Errors Found:
                    </Typography>
                    <div className="space-y-1">
                      {analysisStats.errors.map((error, index) => (
                        <Typography key={index} variant="admin_txt2" className="text-admin_error">
                          • {error}
                        </Typography>
                      ))}
                      {analysisStats.hasMoreErrors && (
                        <Typography variant="admin_txt2" className="text-admin_error/70 italic">
                          ... and more errors
                        </Typography>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-6">
                <div>
                  <DropZone
                    accept={{ 'text/csv': ['.csv'] }}
                    maxFiles={1}
                    onDrop={handleFileDrop}
                    onRender={({
                      isDragging,
                      handleClick,
                      handleDragEnter,
                      handleDragOver,
                      handleDragLeave,
                      handleDrop,
                    }) => (
                      <div
                        onClick={loading ? undefined : handleClick}
                        onDragEnter={handleDragEnter}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        className={`
                          relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all
                          ${file
                            ? 'border-admin_primary bg-admin_primary/5'
                            : isDragging
                              ? 'border-admin_primary bg-admin_primary/10'
                              : 'border-admin_bordered bg-admin_canvas hover:border-admin_primary hover:bg-admin_primary/5'
                          }
                          ${loading ? 'opacity-50 cursor-not-allowed' : ''}
                        `}
                      >
                        {file ? (
                          <div className="space-y-2">
                            <div className="flex items-center justify-center">
                              <svg
                                className="w-12 h-12 text-admin_primary"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                />
                              </svg>
                            </div>
                            <Typography
                              variant="admin_body1"
                              className="text-admin_primary font-medium"
                            >
                              {file.name}
                            </Typography>
                            <Typography variant="admin_txt2_dim">
                              Click to change file
                            </Typography>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <div className="flex items-center justify-center">
                              <ArrowUpTrayIcon className="w-12 h-12 text-admin_text_secondary" />
                            </div>
                            <Typography variant="admin_body1">
                              {isDragging ? 'Drop CSV file here' : 'Click to select CSV file'}
                            </Typography>
                            <Typography variant="admin_txt2_dim">
                              or drag and drop your file here
                            </Typography>
                          </div>
                        )}
                      </div>
                    )}
                  />
                </div>

                {file && (
                  <div className="bg-admin_canvas rounded-lg p-4">
                    <div className="flex items-center space-x-3">
                      <Checkbox
                        variant="admin_compact"
                        isOn={importAsSubscribed}
                        onChange={setImportAsSubscribed}
                        disabled={loading}
                      />

                      <div className="flex-1">
                        <Typography
                          variant="admin_body1"
                          className="cursor-pointer leading-[1.25rem]"
                          onClick={() => !loading && setImportAsSubscribed(!importAsSubscribed)}
                        >
                          Import all as subscribed
                        </Typography>

                        <Typography
                          variant="admin_txt2_dim"
                          className="mt-2 block"
                        >
                          When enabled, all imported subscribers will be marked as active regardless of the CSV data
                        </Typography>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )
          ) : (
            <div className="space-y-6">
              <div className="flex justify-center">
                <div className="w-16 h-16 rounded-full bg-admin_primary/10 flex items-center justify-center">
                  <svg
                    className="w-10 h-10 text-admin_primary"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
              </div>

              <div className="text-center">
                <Typography variant="admin_h3" className="mb-2">
                  Import Complete
                </Typography>
                <Typography variant="admin_txt1_dim">
                  Your subscribers have been successfully imported
                </Typography>
              </div>

              <div className="bg-admin_canvas rounded-lg p-6 text-center">
                <Typography
                  variant="admin_h1"
                  className="text-admin_primary mb-2"
                >
                  {results.imported.toLocaleString()}
                </Typography>
                <Typography
                  variant="admin_body1"
                  className="text-admin_text"
                >
                  New Subscribers
                </Typography>
              </div>

              {results.errors && results.errors.length > 0 && (
                <div className="bg-admin_error/10 border border-admin_error/20 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <svg
                      className="w-5 h-5 text-admin_error flex-shrink-0 mt-0.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <div className="flex-1">
                      <Typography
                        variant="admin_body1"
                        className="text-admin_error font-semibold mb-2"
                      >
                        {results.errors.length} Error{results.errors.length !== 1 ? 's' : ''} Occurred
                      </Typography>
                      <div className="max-h-32 overflow-y-auto space-y-1">
                        {results.errors.slice(0, 5).map((error, index) => (
                          <Typography
                            key={index}
                            variant="admin_txt2"
                            className="text-admin_error"
                          >
                            • {error}
                          </Typography>
                        ))}
                        {results.errors.length > 5 && (
                          <Typography
                            variant="admin_txt2"
                            className="text-admin_error/70 italic"
                          >
                            ... and {results.errors.length - 5} more error{results.errors.length - 5 !== 1 ? 's' : ''}
                          </Typography>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between border-t border-admin_bordered p-6">
          {!results && !showConfirmation && (
            <Button
              variant="admin_secondary"
              onClick={(e) => {
                e.preventDefault()
                setShowFormatExample(true)
              }}
            >
              See example
            </Button>
          )}

          {showConfirmation && !results && (
            <Button
              variant="admin_secondary"
              onClick={() => setShowConfirmation(false)}
              disabled={loading}
            >
              Back
            </Button>
          )}

          {!results ? (
            showConfirmation ? (
              <Button
                variant="admin_primary"
                loading={loading}
                onClick={handleProceedImport}
              >
                Proceed
              </Button>
            ) : (
              file && (
                <Button
                  variant="admin_primary"
                  loading={loading}
                  className="min-w-[120px]"
                  onClick={handleAnalyze}
                >
                  Analyze
                </Button>
              )
            )
          ) : (
            <div className="flex justify-end w-full">
              <Button
                variant="admin_secondary"
                onClick={handleClose}
              >
                Close
              </Button>
            </div>
          )}
        </div>
      </form>

      <CSVFormatExamplePanel
        isOpen={showFormatExample}
        onClose={() => setShowFormatExample(false)}
      />
    </Modal>
  )
}

export default ImportSubscribersDialog
